import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ICONS } from './ui/icons';
import { palette } from '../lib/palette';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { PageSkeleton } from './ui/Skeleton';

interface ProtectedRouteProps {
  children: ReactNode;
  /** @deprecated use `permission` (papel na organização ativa) */
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  /** Permissão exigida na organização ativa (ver backend/src/lib/permissions.ts) */
  permission?: string;
}

function AccessDenied({ detail }: { detail: string }) {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div
          aria-hidden
          style={{
            width: 48, height: 48, margin: '0 auto 16px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: palette.red.light3, color: palette.red.base,
          }}
        >
          <ICONS.Lock size={32} />
        </div>
        <h3 className="text-lg font-semibold" style={{ marginBottom: 8 }}>Acesso negado</h3>
        <p className="text-kumo-subtle">{detail}</p>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false, permission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { can, loading: tenantLoading, roleLabel } = useTenant();

  if (isLoading || (permission && tenantLoading)) {
    return (
      <PageSkeleton />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user?.role !== 'SUPERADMIN') {
    return <AccessDenied detail="Esta área é restrita aos administradores da plataforma." />;
  }

  if (permission && !can(permission)) {
    return <AccessDenied detail={`Seu papel nesta organização${roleLabel ? ` (${roleLabel})` : ''} não dá acesso a esta página.`} />;
  }

  if (adminOnly && !['ADMIN', 'SUPERADMIN'].includes(user?.role || '')) {
    return <AccessDenied detail="Esta área é restrita aos administradores da organização." />;
  }

  return <>{children}</>;
}
